import React from "react";
import { Card, CardHeader, CardBody, Spinner } from "reactstrap";
import { threatCard, threatCardHeader, threatCardBody, threatIconChip } from "./cardStyles";

const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
};

const TopThreatTypes = ({ data, loading }) => {
  if (loading) {
    return (
      <Card 
        className="h-100"
        style={{ 
          border: '1px solid rgba(255, 107, 53, 0.2)', 
          backgroundColor: 'transparent',
          borderRadius: '8px'
        }}
      >
        <CardHeader className="bg-light">
          <h5 className="card-title mb-0">
            <i className="ri-shield-cross-line me-2 text-primary"></i>
            Top Threat Types
          </h5>
        </CardHeader>
        <CardBody className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
          <Spinner style={{ color: ORANGE_COLORS.primary }} />
        </CardBody>
      </Card>
    );
  }

  const getColorByPercentage = (percentage) => {
    if (percentage >= 30) {
      return { bg: 'rgba(139, 0, 0, 0.15)', color: '#8b0000', border: 'rgba(139, 0, 0, 0.3)' }; // Critical - Dark Red
    } else if (percentage >= 20) {
      return { bg: 'rgba(209, 77, 31, 0.15)', color: '#d14d1f', border: 'rgba(209, 77, 31, 0.3)' }; // High - Darker Orange
    } else if (percentage >= 10) {
      return { bg: 'rgba(232, 90, 40, 0.15)', color: '#e85a28', border: 'rgba(232, 90, 40, 0.3)' }; // Medium - Dark Orange
    } else if (percentage >= 5) {
      return { bg: 'rgba(255, 107, 53, 0.15)', color: '#FF5B00', border: 'rgba(255, 107, 53, 0.3)' }; // Low - Primary Orange
    } else {
      return { bg: 'rgba(255, 140, 97, 0.15)', color: '#ff8c61', border: 'rgba(255, 140, 97, 0.3)' }; // Very Low - Light Orange
    }
  };

  const threats = (data || []).map(threat => {
    const percentage = threat.value;
    return { 
      ...threat, 
      colorScheme: getColorByPercentage(percentage)
    };
  });

  const totalThreats = threats.reduce((sum, threat) => sum + (threat.count || 0), 0);

  return (
      <Card 
        className="h-100"
        style={{ 
          ...threatCard
        }}
      >
        <CardHeader style={threatCardHeader}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <div style={threatIconChip()}>
                <i className="ri-shield-cross-line" style={{ color: "var(--vz-primary)", fontSize: 16 }}></i>
              </div>
              <h5 className="card-title mb-0">Top Threat Types</h5>
            </div>
          {totalThreats > 0 && (
            <span 
              className="badge" 
              style={{ 
                backgroundColor: 'rgba(255, 107, 53, 0.1)', 
                color: ORANGE_COLORS.primary,
                border: `1px solid ${ORANGE_COLORS.primary}`,
                fontSize: '11px'
              }}
            >
              {totalThreats} total
            </span>
          )}
        </div>
      </CardHeader>
      <CardBody style={threatCardBody}>
        <div className="py-2">
          {threats.length > 0 ? (
            threats.map((threat, index) => (
              <div key={index} className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
                    <div 
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: threat.colorScheme.color,
                        marginRight: '10px',
                        flexShrink: 0,
                        boxShadow: `0 0 0 2px ${threat.colorScheme.bg}`
                      }}
                    ></div>
                    <span 
                      className="text-muted text-truncate" 
                      style={{ 
                        fontSize: "13px", 
                        fontWeight: "500"
                      }}
                      title={threat.name}
                    >
                      {threat.name}
                    </span>
                  </div>
                  <span 
                    className="badge ms-2" 
                    style={{ 
                      backgroundColor: threat.colorScheme.bg,
                      color: threat.colorScheme.color,
                      border: `1px solid ${threat.colorScheme.border}`,
                      fontSize: "11px", 
                      fontWeight: "600",
                      flexShrink: 0
                    }}
                  >
                    {threat.value}%
                  </span>
                </div>
                <div className="position-relative" style={{ height: "10px", backgroundColor: 'rgba(255, 107, 53, 0.08)', borderRadius: '5px', overflow: 'hidden' }}>
                  <div
                    className="position-absolute top-0 start-0 h-100"
                    style={{
                      width: `${threat.value}%`,
                      background: `linear-gradient(90deg, ${threat.colorScheme.color} 0%, ${threat.colorScheme.color}dd 100%)`,
                      borderRadius: '5px',
                      transition: 'width 0.5s ease',
                      boxShadow: `0 0 8px ${threat.colorScheme.bg}`
                    }}
                  ></div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-5">
            <div className="mx-auto mb-3" style={threatIconChip()}>
              <i className="ri-shield-cross-line" style={{ color: "var(--vz-primary)", fontSize: 16 }}></i>
            </div>
            <p className="text-muted mb-0">No threat data available</p>
          </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default TopThreatTypes;
